import {
  Chapter,
  StoryNode,
  StoryProgress,
  StorySave,
  NotebookEntry
} from '../types/story';
import chapter1Data from '../data/story/chapter1.json';

export class StoryEngine {
  private static instance: StoryEngine;
  private currentChapter: Chapter | null = null;
  private currentNode: StoryNode | null = null;
  private progress: StoryProgress;
  private listeners: Set<(event: string, data: any) => void> = new Set();

  private constructor() {
    this.progress = this.loadProgress();
  }

  static getInstance(): StoryEngine {
    if (!StoryEngine.instance) {
      StoryEngine.instance = new StoryEngine();
    }
    return StoryEngine.instance;
  }

  // 事件监听
  addListener(callback: (event: string, data: any) => void) {
    this.listeners.add(callback);
  }

  removeListener(callback: (event: string, data: any) => void) {
    this.listeners.delete(callback);
  }

  private emit(event: string, data: any) {
    this.listeners.forEach(callback => callback(event, data));
  }

  // 加载章节
  loadChapter(chapterId: string): boolean {
    if (chapterId === 'chapter1') {
      // 直接转换整个chapter1Data
      this.currentChapter = chapter1Data as unknown as Chapter;
      this.currentNode = this.currentChapter?.nodes?.[this.currentChapter?.startNodeId ?? ''] ?? null;
      this.progress.currentChapter = chapterId;
      this.progress.currentNodeId = this.currentChapter?.startNodeId ?? '';
      this.emit('chapterLoaded', { chapter: this.currentChapter });
      return true;
    }
    return false;
  }

  // 获取当前节点
  getCurrentNode(): StoryNode | null {
    return this.currentNode;
  }

  // 获取当前章节
  getCurrentChapter(): Chapter | null {
    return this.currentChapter;
  }

  // 获取进度
  getProgress(): StoryProgress {
    return { ...this.progress };
  }

  // 做出选择
  makeChoice(choiceId: string): boolean {
    if (!this.currentNode || !this.currentNode.choices) {
      return false;
    }

    const choice = this.currentNode?.choices?.find(c => c.id === choiceId);
    if (!choice) {
      return false;
    }

    // 记录选择
    this.progress.choices[this.currentNode.id] = choiceId;

    // 应用选择效果
    if (choice.effects) {
      if (choice.effects.trustChange) {
        // 更新信任度
        this.progress.trust['oldHan'] = (this.progress.trust['oldHan'] || 0) + choice.effects.trustChange;
      }
      if (choice.effects.addNote) {
        this.progress.notes.push(choice.effects.addNote);
      }
      if (choice.effects?.unlockEvidence) {
        const evidenceId = choice.effects.unlockEvidence;
        if (this.progress.evidence[evidenceId] !== undefined) {
          this.progress.evidence[evidenceId] = true;
        }
      }
    }

    // 进入下一个节点
    return this.goToNode(choice.nextNodeId);
  }

  // 发现证据
  discoverEvidence(evidenceId: string): boolean {
    if (this.progress.evidence[evidenceId] !== undefined) {
      this.progress.evidence[evidenceId] = true;
      this.saveProgress();
      this.emit('evidenceDiscovered', { evidenceId });
      return true;
    }
    return false;
  }

  // 跳转到指定节点
  goToNode(nodeId: string): boolean {
    if (!this.currentChapter || !this.currentChapter.nodes[nodeId]) {
      return false;
    }

    this.currentNode = this.currentChapter.nodes[nodeId];
    this.progress.currentNodeId = nodeId;
    this.saveProgress();
    this.emit('nodeChanged', { node: this.currentNode });
    return true;
  }

  // 进入下一个节点
  nextNode(): boolean {
    if (!this.currentNode || !this.currentNode.nextNodeId) {
      return false;
    }
    return this.goToNode(this.currentNode.nextNodeId);
  }

  // 完成章节
  completeChapter(): boolean {
    if (!this.currentChapter) {
      return false;
    }

    if (!this.progress.completedChapters.includes(this.currentChapter.id)) {
      this.progress.completedChapters.push(this.currentChapter.id);
      this.saveProgress();
      this.emit('chapterCompleted', { chapterId: this.currentChapter.id });
    }
    return true;
  }

  // 保存进度
  saveProgress(): void {
    const saveData: StorySave = {
      version: '1.0.0',
      progress: this.progress,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('storyProgress', JSON.stringify(saveData));
  }

  // 加载进度
  loadProgress(): StoryProgress {
    const saved = localStorage.getItem('storyProgress');
    if (saved) {
      try {
        const saveData: StorySave = JSON.parse(saved);
        return saveData.progress;
      } catch (e) {
        console.error('Failed to load progress:', e);
      }
    }

    // 返回默认进度
    return {
      currentChapter: '',
      currentNodeId: '',
      completedChapters: [],
      choices: {},
      evidence: {},
      notes: [],
      trust: {},
      lastSaved: new Date().toISOString()
    };
  }

  // 重置进度
  resetProgress(): void {
    this.progress = {
      currentChapter: '',
      currentNodeId: '',
      completedChapters: [],
      choices: {},
      evidence: {},
      notes: [],
      trust: {},
      lastSaved: new Date().toISOString()
    };
    this.saveProgress();
    this.emit('progressReset', {});
  }

  // 获取章节进度百分比
  getChapterProgress(): number {
    if (!this.currentChapter) {
      return 0;
    }

    const totalNodes = Object.keys(this.currentChapter?.nodes ?? {}).length;

    // 简单计算：基于当前节点位置估算进度
    const nodeIds = Object.keys(this.currentChapter?.nodes ?? {});
    const currentIndex = nodeIds.indexOf(this.progress.currentNodeId);
    return Math.round((currentIndex + 1) / totalNodes * 100);
  }

  // 添加笔记
  addNote(note: string): void {
    this.progress.notes.push(note);
    this.saveProgress();
    this.emit('noteAdded', { note });
  }

  // 获取所有笔记
  getNotes(): NotebookEntry[] {
    return this.progress.notes.map((note, index) => ({
      id: `note_${index}`,
      date: this.progress.lastSaved,
      title: `笔记 ${index + 1}`,
      content: note,
      tags: ['自定义'],
      chapter: this.progress.currentChapter
    }));
  }
}